import React, { useState, useCallback, useRef } from 'react';
import { Theme, createStyles, WithStyles, withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CloudDownload from "@material-ui/icons/CloudDownload";
import Folder from "@material-ui/icons/Folder";
import { uploadSailingSchedule } from '../../lib/api/sailingschedule';

interface UploadedFile {
  name: string;
  size: number;
  base64: string;
}

export interface MyDropzoneRef {
  handleProcess: () => Promise<boolean>;
}

export interface MyDropzoneProps {
  onUploadStart?: () => void;
  onUploadComplete?: (success: boolean, message: string) => void;
}

// uploading files on the arrivals 
const dropzoneStyles = (theme: Theme) =>
  createStyles({
    root: {
      position: "relative",
      padding: "5px",
      border: "dashed 3px #888",
      borderRadius: 5,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    },
    icon: {
      padding: theme.spacing(1),
    },
  });

type DropzoneProps = {
  file: any;
  onDrop: (files: File[]) => void;
  multiple?: boolean;
};

const DropzoneUnstyled: React.FC<DropzoneProps & WithStyles<typeof dropzoneStyles>> = ({ classes, file, onDrop, multiple }) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragActive(false);
    if (e.dataTransfer.files) onDrop(Array.from(e.dataTransfer.files));
  }, [onDrop]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) onDrop(Array.from(e.target.files));
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [onDrop]);

  return (
    <div
      ref={dropZoneRef}
      className={classes.root}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />
      {file ? <Folder /> : <CloudDownload />}
      {file && (isDragActive ? <Typography>Drop the files here ...</Typography> : <Typography>Drag 'n' drop a file here, or click to select file</Typography>)}
    </div>
  );
};

export const Dropzone = withStyles(dropzoneStyles)(DropzoneUnstyled);

// for excel files upload
export function useDropzoneLogic(onUploadStart?: () => void, onUploadComplete?: (success: boolean, message: string) => void) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const isValidFileType = (file: File) => ['.xlsx', '.xls'].some(ext => file.name.toLowerCase().endsWith(ext));

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const processFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    setSuccessMessage(null);
    setErrorMessage(null);
    const fileArray = Array.from(files);
    if (fileArray.some(f => !isValidFileType(f))) {
      setErrorMessage('Invalid file type. Only Excel files (.xlsx, .xls) are allowed.');
      return;
    }
    for (const file of fileArray) {
      try {
        const base64 = await fileToBase64(file);
        setUploadedFiles(prev => [...prev, { name: file.name, size: file.size, base64 }]);
      } catch (error) {
        setErrorMessage('Error reading file: ' + file.name);
      }
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation(); setIsDragActive(false);
    processFiles(e.dataTransfer.files);
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleDropzoneClick = useCallback(() => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isUploading]);

  const handleRemoveFile = (index: number) => {
    if (!isUploading) setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleProcess = async (): Promise<boolean> => {
    if (uploadedFiles.length === 0) { setErrorMessage('No files to upload'); return false; }
    setIsUploading(true); setSuccessMessage(null); setErrorMessage(null);
    if (onUploadStart) onUploadStart();
    let successCount = 0, errorCount = 0;
    for (let i = 0; i < uploadedFiles.length; i++) {
      try { await uploadSailingSchedule(uploadedFiles[i].base64, uploadedFiles[i].name, i === 0); successCount++; }
      catch (error) { errorCount++; }
    }
    setIsUploading(false);
    if (successCount > 0 && errorCount === 0) {
      const message = 'Successfully uploaded ' + successCount + ' file(s)!';
      setSuccessMessage(message);
      if (onUploadComplete) onUploadComplete(true, message);
      setUploadedFiles([]);
      return true;
    } else if (successCount > 0) {
      const message = 'Uploaded ' + successCount + ' file(s), ' + errorCount + ' failed';
      setErrorMessage(message);
      if (onUploadComplete) onUploadComplete(false, message);
      return false;
    } else {
      setErrorMessage('Upload failed');
      if (onUploadComplete) onUploadComplete(false, 'Upload failed');
      return false;
    }
  };

  return {
    uploadedFiles, successMessage, errorMessage, isUploading, isDragActive,
    fileInputRef, dropZoneRef,
    handleDragEnter, handleDragLeave, handleDragOver, handleDrop,
    handleFileInputChange, handleDropzoneClick, handleRemoveFile, handleProcess
  };
}